/* eslint-disable no-console, no-shadow */
// NOTICE: KAPTION-INTEGRATION
// Kaption Feature Flags Vuex Module - Separate from Chatwoot's built-in feature flags
//
// This module integrates with the Kaption Feature Flags API at flags.kaptionai.com
// to provide dynamic, remotely-managed feature flags for the Kaption platform.
//
// Key Features:
// - localStorage caching with TTL
// - Background refresh every 30 seconds
// - Graceful degradation on API failure
// - Per-account and per-user flag resolution
//

import kaptionFlagsAPI from '../../api/kaptionFeatureFlags';
import { KAPTION_FEATURE_FLAGS_CONFIG } from '../../config/kaptionFeatureFlags';

// Helper function to build cache key
function buildCacheKey(accountId, userId) {
  if (userId) {
    return `account:${accountId}:user:${userId}`;
  }
  return `account:${accountId}`;
}

// Helper function to load from localStorage
function loadFromLocalStorage(cacheKey) {
  try {
    const stored = localStorage.getItem(`kaption_feature_flags:${cacheKey}`);
    if (!stored) return null;

    const data = JSON.parse(stored);
    return data.flags || null;
  } catch (error) {
    return null;
  }
}

// Helper function to load metadata from localStorage
function loadMetadataFromLocalStorage(cacheKey) {
  try {
    const stored = localStorage.getItem(
      `kaption_feature_flags:${cacheKey}:metadata`
    );
    if (!stored) return null;

    return JSON.parse(stored);
  } catch (error) {
    return null;
  }
}

// Helper function to save to localStorage
function saveToLocalStorage(cacheKey, flags) {
  try {
    const data = {
      flags,
      timestamp: Date.now(),
      count: flags.length,
    };

    localStorage.setItem(
      `kaption_feature_flags:${cacheKey}`,
      JSON.stringify(data)
    );

    localStorage.setItem(
      `kaption_feature_flags:${cacheKey}:metadata`,
      JSON.stringify({
        timestamp: Date.now(),
        count: flags.length,
      })
    );
  } catch (error) {
    // Silent failure
  }
}

// Helper function to clear localStorage
function clearLocalStorage(cacheKey) {
  try {
    localStorage.removeItem(`kaption_feature_flags:${cacheKey}`);
    localStorage.removeItem(`kaption_feature_flags:${cacheKey}:metadata`);
  } catch (error) {
    // Silent failure
  }
}

// NOTICE: KAPTION-INTEGRATION - Local Override Support
// Local overrides localStorage key
const LOCAL_OVERRIDES_STORAGE_KEY = 'kaption_feature_flag_overrides';

// Helper function to load local overrides from localStorage
function loadLocalOverridesFromStorage() {
  try {
    const stored = localStorage.getItem(LOCAL_OVERRIDES_STORAGE_KEY);
    if (!stored) return {};
    return JSON.parse(stored);
  } catch (error) {
    return {};
  }
}

// Helper function to save local overrides to localStorage
function saveLocalOverridesToStorage(overrides) {
  try {
    if (Object.keys(overrides).length === 0) {
      localStorage.removeItem(LOCAL_OVERRIDES_STORAGE_KEY);
    } else {
      localStorage.setItem(
        LOCAL_OVERRIDES_STORAGE_KEY,
        JSON.stringify(overrides)
      );
    }
  } catch (error) {
    // Silent failure
  }
}

// Initial state (factory function to avoid shared state across store instances)
const state = () => ({
  flags: {}, // In-memory flags: { cacheKey: [flags] }
  cacheMetadata: {}, // Metadata: { cacheKey: { timestamp, count } }
  refreshTimer: null,
  isLoading: false,
  isInitialized: false,
  lastError: null,
  // NOTICE: KAPTION-INTEGRATION - Local overrides for forcing flags
  // Format: { "FLAG_NAME": true/false }
  localOverrides: loadLocalOverridesFromStorage(),
});

// Getters
export const getters = {
  // Synchronous flag check (from cache)
  // NOTICE: KAPTION-INTEGRATION - Checks local overrides first before cached/remote values
  isEnabled: state => (flagName, accountId, userId) => {
    // Check local overrides first (highest priority)
    if (Object.prototype.hasOwnProperty.call(state.localOverrides, flagName)) {
      return state.localOverrides[flagName];
    }

    if (!KAPTION_FEATURE_FLAGS_CONFIG.enabled) return false;
    if (!accountId) return false;

    const cacheKey = buildCacheKey(accountId, userId);
    const flags = state.flags[cacheKey] || loadFromLocalStorage(cacheKey);

    if (!flags || flags.length === 0) {
      // Return fallback
      return KAPTION_FEATURE_FLAGS_CONFIG.fallbackFlags[flagName] || false;
    }

    // Find the flag
    const flag = flags.find(f => f.name === flagName);

    // Prefer remoteEnabled over enabled
    if (flag?.remoteEnabled !== undefined) return flag.remoteEnabled;
    if (flag?.enabled !== undefined) return flag.enabled;

    // Flag not found in remote response, use fallback
    return KAPTION_FEATURE_FLAGS_CONFIG.fallbackFlags[flagName] || false;
  },

  // Get all cached flags for account/user
  getCachedFlags: state => (accountId, userId) => {
    const cacheKey = buildCacheKey(accountId, userId);
    return state.flags[cacheKey] || loadFromLocalStorage(cacheKey) || [];
  },

  // Check if cache is valid
  isCacheValid: state => (accountId, userId) => {
    const cacheKey = buildCacheKey(accountId, userId);
    const metadata =
      state.cacheMetadata[cacheKey] || loadMetadataFromLocalStorage(cacheKey);

    if (!metadata || !metadata.timestamp) return false;

    const age = Date.now() - metadata.timestamp;
    return age < KAPTION_FEATURE_FLAGS_CONFIG.cacheTimeout;
  },

  // Get loading state
  isLoading: state => state.isLoading,

  // Get initialization state
  isInitialized: state => state.isInitialized,

  // Get last error
  getLastError: state => state.lastError,

  // NOTICE: KAPTION-INTEGRATION - Get all local overrides
  getLocalOverrides: state => state.localOverrides,

  // Check if a specific flag has a local override
  hasLocalOverride: state => flagName => {
    return Object.prototype.hasOwnProperty.call(state.localOverrides, flagName);
  },

  // Get the local override value for a specific flag (undefined if not set)
  getLocalOverride: state => flagName => {
    if (Object.prototype.hasOwnProperty.call(state.localOverrides, flagName)) {
      return state.localOverrides[flagName];
    }
    return undefined;
  },
};

// Actions
export const actions = {
  // Initialize the feature flags system
  async initialize({ commit, dispatch }, { accountId, userId }) {
    if (!KAPTION_FEATURE_FLAGS_CONFIG.enabled) {
      return;
    }

    if (!accountId) {
      return;
    }

    // Load from localStorage first so the UI renders instantly with the last
    // known flags (no flash) while we revalidate against the server.
    dispatch('loadFromLocalStorage', { accountId, userId });

    // NOTICE: KAPTION-INTEGRATION - Always revalidate on load.
    // localStorage flags have a 30-minute TTL, so without forceRefresh a fresh
    // page load within that window would keep serving stale flags (e.g. a flag
    // flipped server-side wouldn't take effect until the cache expired). Forcing
    // a refresh here guarantees the dashboard converges on the server's value on
    // every load / account switch, while the cached value above avoids any flash.
    await dispatch('fetchFlags', {
      accountId,
      userId,
      platform: 'web',
      forceRefresh: true,
    });

    // Start background refresh
    dispatch('startBackgroundRefresh', { accountId, userId });

    commit('SET_INITIALIZED', true);
  },

  // Fetch flags from API
  async fetchFlags(
    { commit, getters },
    { accountId, userId, platform = 'web', forceRefresh = false }
  ) {
    if (!KAPTION_FEATURE_FLAGS_CONFIG.enabled) return [];
    if (!accountId) return [];

    const cacheKey = buildCacheKey(accountId, userId);

    // Check if cache is valid and we're not forcing refresh
    if (!forceRefresh && getters.isCacheValid(accountId, userId)) {
      return getters.getCachedFlags(accountId, userId);
    }

    commit('SET_LOADING', true);
    commit('SET_ERROR', null);

    try {
      const response = await kaptionFlagsAPI.fetchFlags({
        accountId,
        userId,
        platform,
      });

      const flags = response.data.flags || [];

      // Cache the flags
      commit('SET_FLAGS', { cacheKey, flags });
      saveToLocalStorage(cacheKey, flags);

      commit('SET_LOADING', false);
      return flags;
    } catch (error) {
      commit('SET_ERROR', error.message);
      commit('SET_LOADING', false);

      // Return cached flags on error
      return getters.getCachedFlags(accountId, userId);
    }
  },

  // Async flag check (refreshes if cache expired)
  async enabledAsync(
    { dispatch, getters },
    { flagName, accountId, userId, platform = 'web' }
  ) {
    if (!KAPTION_FEATURE_FLAGS_CONFIG.enabled) return false;
    if (!accountId) return false;

    // Check if cache is expired
    if (!getters.isCacheValid(accountId, userId)) {
      await dispatch('fetchFlags', { accountId, userId, platform });
    }

    // Return the flag value
    return getters.isEnabled(flagName, accountId, userId);
  },

  // Load flags from localStorage
  loadFromLocalStorage({ commit }, { accountId, userId }) {
    const cacheKey = buildCacheKey(accountId, userId);
    const flags = loadFromLocalStorage(cacheKey);

    if (flags && flags.length > 0) {
      commit('SET_FLAGS', { cacheKey, flags });
    }
  },

  // Start background refresh timer
  startBackgroundRefresh({ state, dispatch }, { accountId, userId }) {
    // Clear existing timer
    if (state.refreshTimer) {
      clearInterval(state.refreshTimer);
    }

    // Start new timer
    const timer = setInterval(() => {
      dispatch('fetchFlags', {
        accountId,
        userId,
        platform: 'web',
        forceRefresh: true,
      });
    }, KAPTION_FEATURE_FLAGS_CONFIG.refreshInterval);

    state.refreshTimer = timer;
  },

  // Stop background refresh
  stopBackgroundRefresh({ state }) {
    if (state.refreshTimer) {
      clearInterval(state.refreshTimer);
      state.refreshTimer = null;
    }
  },

  // Deep reload: Clear cache and fetch fresh flags
  async deepReload(
    { commit, dispatch },
    { accountId, userId, platform = 'web' }
  ) {
    if (!accountId) return;

    const cacheKey = buildCacheKey(accountId, userId);

    // Clear Vuex state
    commit('CLEAR_FLAGS', cacheKey);

    // Clear localStorage
    clearLocalStorage(cacheKey);

    // Fetch fresh flags
    await dispatch('fetchFlags', {
      accountId,
      userId,
      platform,
      forceRefresh: true,
    });
  },

  // Reload flags when account switches
  async reloadForAccount({ dispatch }, { accountId, userId }) {
    if (!accountId) return;

    // Stop current refresh
    dispatch('stopBackgroundRefresh');

    // Deep reload for new account
    await dispatch('deepReload', { accountId, userId });

    // Restart background refresh for new account
    dispatch('startBackgroundRefresh', { accountId, userId });
  },

  // Destroy the module (cleanup)
  destroy({ commit, dispatch }) {
    dispatch('stopBackgroundRefresh');
    commit('CLEAR_ALL');
  },

  // NOTICE: KAPTION-INTEGRATION - Local override actions
  // Set a local override for a flag
  setLocalOverride({ commit, state }, { flagName, value }) {
    commit('SET_LOCAL_OVERRIDE', { flagName, value });
    saveLocalOverridesToStorage(state.localOverrides);
  },

  // Clear a local override for a flag
  clearLocalOverride({ commit, state }, { flagName }) {
    commit('CLEAR_LOCAL_OVERRIDE', { flagName });
    saveLocalOverridesToStorage(state.localOverrides);
  },

  // Clear all local overrides
  clearAllLocalOverrides({ commit }) {
    commit('CLEAR_ALL_LOCAL_OVERRIDES');
    saveLocalOverridesToStorage({});
  },
};

// Mutations
export const mutations = {
  SET_FLAGS(state, { cacheKey, flags }) {
    state.flags = {
      ...state.flags,
      [cacheKey]: flags,
    };

    state.cacheMetadata = {
      ...state.cacheMetadata,
      [cacheKey]: {
        timestamp: Date.now(),
        count: flags.length,
      },
    };
  },

  CLEAR_FLAGS(storeState, cacheKey) {
    const newFlags = { ...storeState.flags };
    const newMetadata = { ...storeState.cacheMetadata };

    delete newFlags[cacheKey];
    delete newMetadata[cacheKey];

    storeState.flags = newFlags;
    storeState.cacheMetadata = newMetadata;
  },

  CLEAR_ALL(state) {
    state.flags = {};
    state.cacheMetadata = {};
    state.isInitialized = false;
  },

  SET_LOADING(state, isLoading) {
    state.isLoading = isLoading;
  },

  SET_ERROR(state, error) {
    state.lastError = error;
  },

  SET_INITIALIZED(state, isInitialized) {
    state.isInitialized = isInitialized;
  },

  // NOTICE: KAPTION-INTEGRATION - Local override mutations
  SET_LOCAL_OVERRIDE(state, { flagName, value }) {
    state.localOverrides = {
      ...state.localOverrides,
      [flagName]: value,
    };
  },

  CLEAR_LOCAL_OVERRIDE(state, { flagName }) {
    const newOverrides = { ...state.localOverrides };
    delete newOverrides[flagName];
    state.localOverrides = newOverrides;
  },

  CLEAR_ALL_LOCAL_OVERRIDES(state) {
    state.localOverrides = {};
  },
};

// Export the module
export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
